import { uiConfig } from '@brand/config/ui.config';
import { Component, computed, inject, model, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { ACQUISITION_SOURCE_VALUES } from '../../../../core/models/api/user.model';
import { CountryApi } from '../../../../core/services/country.api';
import { PeruLocationApi } from '../../../../core/services/peru-location.api';
import { getCountryOptions, getDefaultCountry } from '../../../../core/utils/country';
import { toYYYYMMDD } from '../../../../core/utils/date';
import { PrimaryButton } from '../../../../shared/components/buttons/primary-button/primary-button';
import { DatepickerFormControl } from '../../../../shared/components/form-controls/datepicker-form-control/datepicker-form-control';
import { AutocompleteFormControl } from '../../../../shared/components/form-controls/autocomplete-form-control/autocomplete-form-control';
import { DropdownFormControl } from '../../../../shared/components/form-controls/dropdown-form-control/dropdown-form-control';
import { ArrowLeftOutline } from '../../../../shared/components/icons/arrow-left-outline';
import { ArrowRightOutline } from '../../../../shared/components/icons/arrow-right-outline';
import { ClockOutline } from '../../../../shared/components/icons/clock-outline';
import { CloseCircleOutline } from '../../../../shared/components/icons/close-circle-outline';
import { SendTwo } from '../../../../shared/components/icons/send-two';
import { ShieldLockBold } from '../../../../shared/components/icons/shield-lock-bold';
import { Image } from '../../../../shared/components/image/image';
import { Tag } from '../../../../shared/components/tag/tag';
import { ModalBase } from '../../../../shared/directives/modal-base';
import { getDepartmentOptions } from '../../../../shared/helpers/peru-location';
import { DropdownOption } from '../../../../shared/models/form-controls.model';
import { UserState } from '../../services/user.state';

export interface ProfileDemographicAnswers {
  usuario_origen_captacion: string;
  usuario_fecha_nacimiento: string;
  usuario_pais: string;
  usuario_departamento?: string;
}

type WizardStep = 0 | 1 | 2 | 3;
const PROFILE_DEMOGRAPHIC_SNOOZE_MAX = 3;
const PERU_COUNTRY_NAME = getDefaultCountry().country_name;

type ProfileDemographicStepKey =
  | 'welcome'
  | 'acquisitionSource'
  | 'birthDate'
  | 'location';

interface ProfileDemographicStepContent {
  key: ProfileDemographicStepKey;
  titleParts: ProfileDemographicTitlePart[];
  description: string;
}

interface ProfileDemographicTitlePart {
  text: string;
  gradient?: boolean;
}

const PROFILE_DEMOGRAPHIC_STEPS: Record<
  WizardStep,
  ProfileDemographicStepContent
> = {
  0: {
    key: 'welcome',
    titleParts: [
      { text: 'Ayúdanos a ', gradient: true },
      { text: 'conocerte mejor' },
    ],
    description:
      'Responde unas preguntas para ofrecerte una experiencia más relevante.',
  },
  1: {
    key: 'acquisitionSource',
    titleParts: [
      { text: '¿Cómo ' },
      { text: 'conociste nuestra plataforma', gradient: true },
      { text: '?' },
    ],
    description: 'Tu respuesta nos ayuda a llegar a más profesionales.',
  },
  2: {
    key: 'birthDate',
    titleParts: [
      { text: '¿Cuál es tu ' },
      { text: 'fecha de nacimiento', gradient: true },
      { text: '?' },
    ],
    description: 'La usamos para comprender mejor a nuestra comunidad.',
  },
  3: {
    key: 'location',
    titleParts: [
      { text: '¿En qué ' },
      { text: 'país resides', gradient: true },
      { text: '?' },
    ],
    description:
      'Así adaptamos la información y oportunidades a tu ubicación.',
  },
};

@Component({
  selector: 'profile-demographic-modal',
  imports: [
    PrimaryButton,
    DatepickerFormControl,
    AutocompleteFormControl,
    DropdownFormControl,
    Image,
    Tag,
    CloseCircleOutline,
    ShieldLockBold,
  ],
  templateUrl: './profile-demographic-modal.html',
  styleUrl: './profile-demographic-modal.scss',
})
export class ProfileDemographicModal extends ModalBase<ProfileDemographicAnswers> {
  readonly buttonVariant = uiConfig.primaryButtonVariant;

  private userState = inject(UserState);
  private countryApi = inject(CountryApi);
  private peruLocationApi = inject(PeruLocationApi);

  ClockOutline = ClockOutline;
  ArrowLeftOutline = ArrowLeftOutline;
  ArrowRightOutline = ArrowRightOutline;
  SendTwo = SendTwo;

  step = model<WizardStep>(0);
  steps = [1, 2, 3] as const;
  loading = signal(false);
  navDir = signal<'forward' | 'back'>('forward');
  acquisitionSource = signal<string | null>(null);
  birthDate = signal<Date | null>(null);
  country = signal<string | null>(null);
  department = signal<string | null>(null);
  countries = toSignal(this.countryApi.getCountries(), { initialValue: [] });
  departments = toSignal(this.peruLocationApi.getDepartments(), {
    initialValue: [],
  });

  acquisitionSourceOptions: DropdownOption<string>[] =
    ACQUISITION_SOURCE_VALUES.map(value => ({
      label: value,
      value,
    }));
  countryOptions = computed<DropdownOption<string>[]>(() =>
    getCountryOptions(this.countries())
  );
  departmentOptions = computed<DropdownOption<string>[]>(() =>
    getDepartmentOptions(this.departments())
  );

  currentStep = computed(() => PROFILE_DEMOGRAPHIC_STEPS[this.step()]);
  isPeru = computed(() => this.country() === PERU_COUNTRY_NAME);
  isFinalStep = computed(() => this.step() === 3);
  canDismiss = computed(
    () =>
      (this.userState.user()?.usuario_perfil_demografico_veces_pospuesto ?? 0) <
      PROFILE_DEMOGRAPHIC_SNOOZE_MAX
  );
  imageSrc = computed(() => {
    return uiConfig.profileDemographicPrompt.images[this.currentStep().key];
  });

  canContinue = computed(() => {
    switch (this.step()) {
      case 0:
        return true;
      case 1:
        return !!this.acquisitionSource();
      case 2:
        return !!this.birthDate();
      case 3:
        return !!this.country() && (!this.isPeru() || !!this.department());
      default:
        return false;
    }
  });

  onDismiss() {
    if (this.loading() || !this.canDismiss()) return;
    this.exit();
  }

  onBack() {
    const current = this.step();
    if (current <= 0 || this.loading()) return;
    this.navDir.set('back');
    this.step.set((current - 1) as WizardStep);
  }

  onNext() {
    if (!this.canContinue() || this.loading()) return;
    const current = this.step();
    if (current === 3) {
      this.submitAnswers();
      return;
    }
    this.navDir.set('forward');
    this.step.set((current + 1) as WizardStep);
  }

  private submitAnswers() {
    const source = this.acquisitionSource();
    const birthDate = toYYYYMMDD(this.birthDate());
    const country = this.country();
    const department = this.department();
    if (!source || !birthDate || !country || (this.isPeru() && !department)) {
      return;
    }

    const payload: ProfileDemographicAnswers = {
      usuario_origen_captacion: source,
      usuario_fecha_nacimiento: birthDate,
      usuario_pais: country,
      ...(this.isPeru() && department && { usuario_departamento: department }),
    };

    this.loading.set(true);
    this.userState
      .update(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe(() => this.send(payload));
  }
}
